import { h } from '@omni-torn/ui';
import { APPROVAL_LABEL, isSnowflake, SERVER_KINDS, type ApprovalState, type BotCaps, type ServerKind } from '@omni-torn/shared';
import { server } from '../session';
import { box } from '../ui';
import { capacityUnseen, serverCapacityBox, setCapacityUnseen } from './server-capacity';
import { tiersBox } from './owner-tiers';

/** Owner only: who sees which website section, and where to change it. */
export function renderOwner(view: HTMLElement): void {
  view.replaceChildren(
    h('div', { class: 'page-head' }, h('h1', {}, 'Owner')),
    // The server's capacity (owner's ask 2026-11-02): disk, memory, CPU, when to upgrade; its warnings join the badge.
    serverCapacityBox(() => {
      badgeAt = Date.now();
      const nav = document.getElementById('sidenav');
      if (nav) ownerBadge(nav);
    }),
    // The one features × tiers table, personal and faction tiers (decision 12) — website sections and the bot here.
    tiersBox(),
    box('Discord bot', h('p', {}, 'Server admins open the bot’s control panel with ', h('code', {}, '/omni panel'), ' in Discord (it gives them a private link to ', h('a', { href: '/panel' }, '/panel'), '). The public page with the invite links: ', h('a', { href: '/bot' }, '/bot'), '.')),
    botServers(),
  );
}

// ---------------------------------------------------------------- the bot's approved servers (public bot Phase B, F3, G1)

interface ServerRow {
  id: string;
  name: string | null;
  members: number | null;
  kind: ServerKind | null;
  factionId: number | null;
  legacy: boolean;
  setUp: boolean;
  terms: { version: number; tornId: number | null; at: number } | null;
  parts: string[];
  present: boolean;
  approval: ApprovalState;
  ready: boolean;
  exception: { decision: 'approved' | 'blocked'; note: string; at: number } | null;
}
interface ServerList {
  open: boolean;
  updatedAt: number | null;
  bot: boolean;
  servers: ServerRow[];
  users?: Array<{ id: string; note: string; at: number }>;
  caps?: BotCaps;
  capLimits?: Record<keyof BotCaps, { min: number; max: number; label: string }>;
}

/** One server's health as the bot sees it (G2) — never its settings. */
interface ServerHealth {
  id: string;
  lastRound: number | null;
  problems: string[];
  invalid: number;
  tornToday: number;
  alertsToday: number;
  support: boolean;
}
interface BotHealth {
  at: number;
  version: string;
  startedAt: number;
  servers: number;
  waiting: number;
  factionsPolled: number;
  round: { ms: number; skipped: number; at: number } | null;
  tornPerMinute: { faction: number; lent: number; own: number };
  tornToday: { faction: number; lent: number; own: number };
  ownCappedMinutes: number;
  alertsToday: number;
  discord: { invalid10m: number; braking: boolean };
  queues: { members: number };
  memory: { rssMb: number; heapMb: number };
  dbMb: number | null;
  notices: Array<{ kind: string; at: number; text: string }>;
  unseen: number;
  perServer: ServerHealth[];
}

const PART_NAMES: Record<string, string> = { gw: 'Giveaways', fac: 'Faction', bank: 'Banking', stake: 'Stakeouts' };
const when = (ms: number) => new Date(ms).toLocaleString();

// The Owner tab's badge in the menu (G2): notices about the bot, and the server's capacity warnings, not seen yet. Looked
// up at most every 5 minutes.
let badgeAt = 0;
let badgeCount = 0;
/** Draws the badge on the menu's Owner link (owner only). */
export function ownerBadge(nav: HTMLElement): void {
  const draw = () => {
    const link = nav.querySelector('[data-section=owner] .nav-name');
    if (!link) return;
    link.querySelector('.nav-badge')?.remove();
    const n = badgeCount + capacityUnseen();
    if (n > 0) link.append(h('span', { class: 'nav-badge', 'data-field': 'owner-badge', title: 'Notices about the Discord bot and the server' }, String(n)));
  };
  draw();
  if (Date.now() - badgeAt < 5 * 60_000) return;
  badgeAt = Date.now();
  // Only the counts (light): the full health is asked when the owner opens it on the Owner tab.
  void server<{ unseen: number }>('GET', '/api/owner/bot-health/unseen')
    .then((r) => {
      badgeCount = r.unseen ?? 0;
      draw();
    })
    .catch(() => undefined);
  void server<{ unseen: number }>('GET', '/api/owner/capacity/unseen')
    .then((r) => {
      setCapacityUnseen(r.unseen ?? 0);
      draw();
    })
    .catch(() => undefined);
}

/**
 * The bot's health (G2 — the bot's private /sync/health): servers, the approval queue, faction rounds, Torn calls by who
 * paid, Discord's refusals, memory, the database; the notices for the owner (rounds over 15 s, refusals climbing, his key's
 * cap hit for an hour) with "Mark as seen" (the menu's badge). Never a server's settings.
 */
function botHealthBox(onHealth: (h: BotHealth | null) => void): HTMLElement {
  const body = h('div', { 'data-field': 'bot-health' }, h('p', { class: 'muted' }, 'Loading…'));
  const draw = (r: { bot: boolean; health: BotHealth | null }) => {
    onHealth(r.health);
    const x = r.health;
    if (!x) return body.replaceChildren(h('p', { class: 'notice bad', 'data-field': 'health-down' }, 'The bot isn’t answering.'));
    badgeCount = x.unseen;
    const cell = (label: string, value: string | number, field: string) => h('div', { 'data-stat': field }, h('strong', {}, String(value)), h('span', { class: 'muted small' }, label));
    const seen = h('button', { type: 'button', class: 'btn-small', 'data-action': 'notices-seen', hidden: !x.unseen }, 'Mark as seen') as HTMLButtonElement;
    seen.addEventListener('click', () =>
      void server('POST', '/api/owner/bot-health/seen').then(() => {
        badgeCount = 0;
        badgeAt = Date.now();
        const nav = document.getElementById('sidenav');
        if (nav) ownerBadge(nav);
        void load();
      }),
    );
    body.replaceChildren(
      h(
        'div',
        { class: 'bot-health-grid' },
        cell('servers', x.servers, 'servers'),
        cell('waiting for approval', x.waiting, 'waiting'),
        cell('factions polled', x.factionsPolled, 'factions'),
        cell('last round', x.round ? `${(x.round.ms / 1000).toFixed(1)} s` : '—', 'round'),
        cell('Torn calls a minute (faction · lent · yours)', `${x.tornPerMinute.faction} · ${x.tornPerMinute.lent} · ${x.tornPerMinute.own}`, 'torn-minute'),
        cell('Torn calls today (faction · lent · yours)', `${x.tornToday.faction} · ${x.tornToday.lent} · ${x.tornToday.own}`, 'torn-today'),
        cell('alerts posted today', x.alertsToday, 'alerts'),
        cell('Discord refusals (10 min)', `${x.discord.invalid10m}${x.discord.braking ? ' — braking' : ''}`, 'invalid'),
        cell('members queued', x.queues.members, 'queued'),
        cell('memory (RSS)', `${x.memory.rssMb} MB`, 'memory'),
        cell('database', x.dbMb === null ? '—' : `${x.dbMb} MB`, 'db'),
        cell('bot version', x.version, 'version'),
      ),
      h('h3', {}, 'Notices ', x.unseen ? h('span', { class: 'nav-badge' }, String(x.unseen)) : null),
      x.notices.length
        ? h('ul', { class: 'plain-list', 'data-field': 'notices' }, ...x.notices.map((n) => h('li', { 'data-notice': n.kind }, h('span', { class: 'muted small' }, `${when(n.at)} — `), n.text)))
        : h('p', { class: 'muted', 'data-field': 'notices' }, 'None in the last 7 days.'),
      seen,
    );
  };
  const load = () =>
    server<{ bot: boolean; health: BotHealth | null }>('GET', '/api/owner/bot-health')
      .then(draw)
      .catch((err: unknown) => body.replaceChildren(h('p', { class: 'notice bad' }, (err as Error).message)));
  // Asked only when he opens it (it looks at every server) — each server's health then shows in the list below too.
  const details = h('details', { 'data-field': 'health-details' }, h('summary', { 'data-action': 'show-health' }, `Show the bot’s health${badgeCount ? ` (${badgeCount} new notice${badgeCount === 1 ? '' : 's'})` : ''}`), body) as HTMLDetailsElement;
  let loaded = false;
  details.addEventListener('toggle', () => {
    if (details.open && !loaded) {
      loaded = true;
      void load();
    }
  });
  return box('Discord bot health', [h('p', { class: 'muted' }, 'Numbers only — never a server’s settings (those only through the control panel, read-only unless a server grants support access).'), details], { id: 'bot-health' });
}

/**
 * Discord servers: which may use the bot's parts. Closed at first, like the extension's access levels (owner's decision
 * 2026-11-01): "Every server" opens it to all; each server can be an exception either way (approve while closed, block —
 * its parts go off with one note; the bot never leaves by itself — owner, 2026-11-02). Each server's health (G2), his manual Leave, blocked Discord users and the global
 * caps (F3, G1).
 */
function botServers(): HTMLElement {
  const body = h('div', { 'data-field': 'bot-servers' }, h('p', { class: 'muted' }, 'Loading…'));
  const status = h('span', { class: 'save-status', role: 'status' });
  let last: ServerList | null = null;
  let health = new Map<string, ServerHealth>();
  const send = async (change: Record<string, unknown>, path = '/api/owner/bot-servers', method: 'PUT' | 'POST' = 'PUT') => {
    status.textContent = 'Saving…';
    try {
      draw(await server<ServerList>(method, path, change));
      status.textContent = 'Saved.';
    } catch (err) {
      status.textContent = (err as Error).message;
    }
  };
  const draw = (l: ServerList) => {
    last = l;
    const open = h('input', { type: 'checkbox', checked: l.open, 'data-field': 'open-all' }) as HTMLInputElement;
    open.addEventListener('change', () => void send({ open: open.checked }));
    const idIn = h('input', { type: 'text', inputmode: 'numeric', placeholder: 'Discord server id', maxlength: '21', class: 'srv-id', 'aria-label': 'Discord server id' }) as HTMLInputElement;
    const noteIn = h('input', { type: 'text', placeholder: 'Note (who it is)', maxlength: '200', class: 'srv-note', 'aria-label': 'Note' }) as HTMLInputElement;
    const add = h('button', { type: 'button', class: 'btn-small', 'data-action': 'approve-id' }, 'Approve') as HTMLButtonElement;
    add.addEventListener('click', () => {
      const id = idIn.value.trim();
      if (!isSnowflake(id)) {
        status.textContent = 'A Discord server id is 15–21 digits (right-click the server → Copy Server ID).';
        return;
      }
      void send({ id, decision: 'approved', note: noteIn.value.trim() });
    });
    const card = (r: ServerRow) => {
      const btn = (label: string, decision: 'approved' | 'blocked' | null, action: string) => {
        const b = h('button', { type: 'button', class: 'btn-small', 'data-action': action }, label) as HTMLButtonElement;
        b.addEventListener('click', () => void send({ id: r.id, decision, note: r.exception?.note ?? '' }));
        return b;
      };
      const leave = h('button', { type: 'button', class: 'btn-small', 'data-action': 'leave', title: 'Only you can make the bot leave (it never leaves by itself): a polite note first; the server can add it back' }, 'Leave') as HTMLButtonElement;
      leave.addEventListener('click', () => {
        if (window.confirm(`Make the bot leave ${r.name ?? r.id}? It never leaves by itself — only this button. The server can add it back (blocked, its parts stay off).`)) void send({ id: r.id }, '/api/owner/bot-servers/leave', 'POST');
      });
      const facts = [
        r.present ? (r.members !== null ? `${r.members} members` : null) : 'the bot isn’t in it',
        r.kind ? SERVER_KINDS[r.kind]?.name ?? r.kind : r.present ? 'kind not picked' : null,
        r.factionId ? `faction ${r.factionId}` : null,
        r.terms ? `terms v${r.terms.version} agreed${r.terms.tornId ? ` by [${r.terms.tornId}]` : ''} ${new Date(r.terms.at).toLocaleDateString()}` : r.legacy ? 'from before setup' : r.present ? 'setup not finished' : null,
        r.parts.length ? `parts: ${r.parts.map((p) => PART_NAMES[p] ?? p).join(', ')}` : null,
      ].filter(Boolean);
      const hl = health.get(r.id);
      const healthLine = hl
        ? h(
            'div',
            { class: 'srv-health small', 'data-field': 'server-health' },
            h('span', { class: 'muted' }, [`last round ${hl.lastRound ? when(hl.lastRound) : '—'}`, `${hl.invalid} Discord refusals (10 min)`, `${hl.tornToday} Torn calls today`, `${hl.alertsToday} alerts today`, hl.support ? 'support access granted' : null].filter(Boolean).join(' · ')),
            hl.problems.length ? h('ul', { class: 'notice', 'data-field': 'server-problems' }, ...hl.problems.map((p) => h('li', {}, p.replace(/\*\*|`/g, '')))) : null,
          )
        : null;
      return h(
        'li',
        { class: `srv srv-${r.approval}`, 'data-server': r.id },
        h('div', { class: 'srv-head' }, h('strong', { class: 'srv-name' }, r.name ?? `Server ${r.id}`), h('span', { class: `badge${r.ready ? ' on' : ''}`, 'data-field': 'approval' }, APPROVAL_LABEL[r.approval])),
        h('div', { class: 'muted small srv-id-line' }, r.id),
        facts.length ? h('div', { class: 'muted small' }, facts.join(' · ')) : null,
        healthLine,
        r.exception?.note ? h('div', { class: 'small' }, `Note: ${r.exception.note}`) : null,
        h(
          'div',
          { class: 'srv-actions' },
          r.exception?.decision !== 'approved' ? btn('Approve', 'approved', 'approve') : null,
          r.exception?.decision !== 'blocked' ? btn('Block', 'blocked', 'block') : null,
          r.exception ? btn(l.open ? 'Back to open' : r.legacy ? 'Back to “from before”' : 'Back to waiting', null, 'reset') : null,
          r.present ? leave : null,
        ),
      );
    };
    // Blocked Discord users (G1): the bot answers them nothing but /account; servers they own have its parts off.
    const userIn = h('input', { type: 'text', inputmode: 'numeric', placeholder: 'Discord user id', maxlength: '21', class: 'srv-id', 'aria-label': 'Discord user id', 'data-field': 'user-id' }) as HTMLInputElement;
    const userNote = h('input', { type: 'text', placeholder: 'Why', maxlength: '200', class: 'srv-note', 'aria-label': 'Why' }) as HTMLInputElement;
    const blockUser = h('button', { type: 'button', class: 'btn-small', 'data-action': 'block-user' }, 'Block user') as HTMLButtonElement;
    blockUser.addEventListener('click', () => {
      const id = userIn.value.trim();
      if (!isSnowflake(id)) {
        status.textContent = 'A Discord user id is 15–21 digits (right-click the user → Copy User ID).';
        return;
      }
      void send({ user: id, blocked: true, note: userNote.value.trim() });
    });
    const users = l.users ?? [];
    // Global caps (F3).
    const limits = l.capLimits;
    const capInputs = limits && l.caps ? (Object.keys(limits) as Array<keyof BotCaps>).map((k) => ({ k, input: h('input', { type: 'number', min: limits[k].min, max: limits[k].max, step: 1, value: String(l.caps![k]), 'data-cap': k }) as HTMLInputElement })) : [];
    const saveCaps = h('button', { type: 'button', class: 'btn-small', 'data-action': 'save-caps' }, 'Save caps') as HTMLButtonElement;
    saveCaps.addEventListener('click', () => void send({ caps: Object.fromEntries(capInputs.map(({ k, input }) => [k, Number(input.value)])) }));
    const waiting = l.servers.filter((x) => x.present && x.approval === 'waiting').length;
    body.replaceChildren(
      h('label', { class: 'switch' }, open, ' ', h('strong', {}, 'Every server'), h('span', { class: 'muted' }, ' — open the bot to all (exceptions below still apply). Off: only approved servers and servers from before.')),
      ...(l.bot ? [] : [h('p', { class: 'notice bad', 'data-field': 'bot-down' }, 'The bot isn’t answering, so only your exceptions are listed.')]),
      h('p', { 'data-field': 'queue', class: waiting ? 'notice' : 'muted' }, waiting ? `Approval queue: ${waiting} server${waiting === 1 ? '' : 's'} waiting (listed first).` : 'Approval queue: none waiting.'),
      h('div', { class: 'srv-add' }, idIn, noteIn, add),
      l.servers.length ? h('ul', { class: 'plain-list srv-list' }, ...l.servers.map(card)) : h('p', { class: 'muted', 'data-field': 'no-servers' }, 'No servers yet.'),
      h('h3', {}, 'Blocked Discord users'),
      h('p', { class: 'muted small' }, 'The bot answers them nothing but /account (their keys, “Delete my data”), and switches its parts off in any server they own. Blocking a server above switches its parts off there (with one note) — the bot never leaves by itself.'),
      h('div', { class: 'srv-add' }, userIn, userNote, blockUser),
      users.length
        ? h(
            'ul',
            { class: 'plain-list srv-list', 'data-field': 'blocked-users' },
            ...users.map((u) => {
              const un = h('button', { type: 'button', class: 'btn-small', 'data-action': 'unblock-user' }, 'Unblock') as HTMLButtonElement;
              un.addEventListener('click', () => void send({ user: u.id, blocked: false }));
              return h('li', { class: 'srv', 'data-user': u.id }, h('div', { class: 'srv-head' }, h('strong', {}, u.id), h('span', { class: 'muted small' }, ` ${new Date(u.at).toLocaleDateString()}`)), u.note ? h('div', { class: 'small' }, u.note) : null, h('div', { class: 'srv-actions' }, un));
            }),
          )
        : h('p', { class: 'muted', 'data-field': 'blocked-users' }, 'Nobody blocked.'),
      ...(capInputs.length
        ? [h('h3', {}, 'Global caps'), h('div', { class: 'caps-form', 'data-field': 'caps' }, ...capInputs.map(({ k, input }) => h('label', {}, h('span', { class: 'small' }, `${limits![k].label} (${limits![k].min}–${limits![k].max})`), input))), saveCaps]
        : []),
      status,
    );
  };
  void server<ServerList>('GET', '/api/owner/bot-servers')
    .then(draw)
    .catch((err: unknown) => body.replaceChildren(h('p', { class: 'notice bad' }, (err as Error).message)));
  const healthBox = botHealthBox((x) => {
    health = new Map((x?.perServer ?? []).map((p) => [p.id, p]));
    if (last) draw(last);
  });
  return h(
    'div',
    {},
    healthBox,
    box('Discord servers', [h('p', { class: 'muted' }, 'Which servers may use the bot’s parts. Closed at first, like the extension’s levels: approve servers one by one, or open it to every server. In a server that isn’t approved only linking, ', h('code', {}, '/account'), ' and ', h('code', {}, '/omni setup'), ' work.'), body], { id: 'bot-servers' }),
  );
}
